import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Avatar,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  ShoppingCart,
  Inventory,
  CalendarToday,
  People,
  Receipt,
  Payment,
  AttachMoney,
  Settings,
  PersonAdd,
  Logout,
  Business,
  Assessment,
  AccountCircle,
  Description,
  ContactMail,
  EventNote,
  CloudDownload,
  Backup as BackupIcon,
  Security as SecurityIcon,
  LocationOn,
  Handshake,
  Code as CodeIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import GlobalSearch from './GlobalSearch';
import ThemeToggle from './ThemeToggle';
import NotificationCenter from './NotificationCenter';
import MobileNav from './MobileNav';
import RealtimeIndicator from './RealtimeIndicator';
import HelpSearch from './HelpSearch';
import LocationSelector from './LocationSelector';
import { useResponsive } from '../hooks/useResponsive';

const drawerWidth = 260;

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', adminOnly: false },
  { text: 'Sales', icon: <ShoppingCart />, path: '/sales', adminOnly: false },
  { text: 'Inventory', icon: <Inventory />, path: '/inventory', adminOnly: false },
  { text: 'Consignments', icon: <Handshake />, path: '/consignments', adminOnly: false },
  { text: 'Calendar', icon: <CalendarToday />, path: '/calendar', adminOnly: false },
  { text: 'Appointments', icon: <EventNote />, path: '/appointments', adminOnly: false },
  { text: 'Staff Schedule', icon: <People />, path: '/staff-schedule', adminOnly: false },
  { text: 'Leads', icon: <People />, path: '/leads', adminOnly: false },
  { text: 'Clients', icon: <AccountCircle />, path: '/clients', adminOnly: false },
  { text: 'Invoices', icon: <Receipt />, path: '/invoices', adminOnly: false },
  { text: 'Payments', icon: <Payment />, path: '/payments', adminOnly: false },
  { text: 'Commissions', icon: <AttachMoney />, path: '/commissions', adminOnly: false },
  { text: 'Documents', icon: <Description />, path: '/documents', adminOnly: false },
];

const adminMenuItems = [
  { text: 'Locations', icon: <LocationOn />, path: '/locations' },
  { text: 'Contact Requests', icon: <ContactMail />, path: '/contact-requests' },
  { text: 'Reports', icon: <Assessment />, path: '/reports' },
  { text: 'Data Export', icon: <CloudDownload />, path: '/export', adminOnly: false },
  { text: 'Backup & Restore', icon: <BackupIcon />, path: '/backup', adminOnly: true },
  { text: 'API Documentation', icon: <CodeIcon />, path: '/api-docs' },
  { text: 'Settings', icon: <Settings />, path: '/settings' },
  { text: 'Users', icon: <PersonAdd />, path: '/users' },
  { text: 'Roles', icon: <SecurityIcon />, path: '/roles' },
];

const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();
  const { isMobile } = useResponsive();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const drawer = (
    <Box>
      <Toolbar
        sx={{
          background: (theme) =>
            theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #2c5f8f 0%, #4d8fcc 100%)'
              : 'linear-gradient(135deg, #003366 0%, #1a4d7a 100%)',
          color: 'white',
        }}
      >
        <Business sx={{ mr: 2 }} />
        <Typography variant="h6" noWrap component="div">
          PaceEstate
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'rgba(0, 51, 102, 0.08)',
                  borderRight: '3px solid',
                  borderColor: 'primary.main',
                },
              }}
            >
              <ListItemIcon sx={{ color: location.pathname === item.path ? 'primary.main' : 'inherit' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      {isAdmin && (
        <>
          <Divider />
          <List>
            <ListItem>
              <ListItemText
                primary="ADMINISTRATION"
                primaryTypographyProps={{
                  variant: 'caption',
                  color: 'text.secondary',
                  fontWeight: 600,
                }}
              />
            </ListItem>
            {adminMenuItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => navigate(item.path)}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(0, 51, 102, 0.08)',
                      borderRight: '3px solid',
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: location.pathname === item.path ? 'primary.main' : 'inherit' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: 'background.paper',
          color: 'text.primary',
          boxShadow: (theme) =>
            theme.palette.mode === 'dark'
              ? '0 1px 3px rgba(0,0,0,0.4)'
              : '0 1px 3px rgba(0,0,0,0.12)',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ display: { xs: 'none', sm: 'block' }, flexGrow: { xs: 1, sm: 0 } }}>
            {menuItems.find((item) => item.path === location.pathname)?.text ||
             adminMenuItems.find((item) => item.path === location.pathname)?.text ||
             'Dashboard'}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {!isMobile && (
            <Box sx={{ mr: 2, minWidth: 200 }}>
              <LocationSelector variant="chip" size="small" />
            </Box>
          )}
          {!isMobile && <GlobalSearch />}
          <NotificationCenter />
          <HelpSearch />
          <RealtimeIndicator />
          {!isMobile && <ThemeToggle />}
          <Tooltip title="Account settings">
            <IconButton onClick={handleMenuOpen} sx={{ ml: { xs: 1, sm: 2 } }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                {user?.full_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
              </Avatar>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
          >
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                {user?.full_name || user?.email}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { handleMenuClose(); navigate('/profile'); }}>
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            {isMobile && (
              <MenuItem onClick={() => { handleMenuClose(); }}>
                <ListItemIcon>
                  <Settings fontSize="small" />
                </ListItemIcon>
                <ThemeToggle inline />
              </MenuItem>
            )}
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: 7, sm: 8 },
          mb: { xs: 10, md: 0 },
          minHeight: '100vh',
          backgroundColor: 'background.default',
        }}
      >
        {children}
      </Box>
      {isMobile && <MobileNav />}
    </Box>
  );
};

export default Layout;
